import { useSearchParams } from 'react-router-dom';
import { ORDER_ID_STORAGE_KEY } from './catalog.js';

export function useOrderIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('orderId') ?? params.get('id') ?? getStoredOrderId();
}

export function getStoredOrderId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(ORDER_ID_STORAGE_KEY);
}

export function setStoredOrderId(orderId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(ORDER_ID_STORAGE_KEY, orderId);
}

export function orderBoardPath(): string {
  return '/order-board';
}

/** Binding / notification URL for SCR-MER-051. IncomingOrderPage redirects this onto the board overlay. */
export function incomingOrderPath(orderId: string): string {
  return `/incoming-order?orderId=${encodeURIComponent(orderId)}`;
}

/** 07-03: 051 overlay on the board uses `?incoming=` so Back closes it. */
export const INCOMING_OVERLAY_PARAM = 'incoming';

function cloneSearchParams(search: string | URLSearchParams): URLSearchParams {
  if (typeof search === 'string') {
    return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  }
  return new URLSearchParams(search);
}

export function incomingOverlayIdFromSearch(search: string | URLSearchParams): string | null {
  const value = cloneSearchParams(search).get(INCOMING_OVERLAY_PARAM)?.trim();
  return value ? value : null;
}

export function applyIncomingOverlaySearch(
  search: string | URLSearchParams,
  orderId: string | null,
): URLSearchParams {
  const params = cloneSearchParams(search);
  if (orderId) params.set(INCOMING_OVERLAY_PARAM, orderId);
  else params.delete(INCOMING_OVERLAY_PARAM);
  return params;
}

/** Primary 051 path: modal over the live board (07-03). */
export function incomingBoardPath(orderId: string): string {
  return `${orderBoardPath()}?${applyIncomingOverlaySearch('', orderId).toString()}`;
}

/** Push a history entry on first open; replace when switching ids so Back still closes. */
export function incomingOverlayHistoryMode(
  currentIncomingId: string | null,
  nextOrderId: string,
): 'push' | 'replace' | 'none' {
  if (!nextOrderId) return 'none';
  if (currentIncomingId === nextOrderId) return 'none';
  if (currentIncomingId) return 'replace';
  return 'push';
}

export function rejectOrderPath(orderId: string): string {
  return `/reject-order?orderId=${encodeURIComponent(orderId)}`;
}

export function orderDetailMerchantPath(orderId: string, opts?: { from?: 'history' }): string {
  const params = new URLSearchParams({ orderId });
  if (opts?.from) params.set('from', opts.from);
  return `/order-detail-merchant?${params.toString()}`;
}

export function adjustPromisedTimePath(orderId: string): string {
  return `/adjust-promised-time?orderId=${encodeURIComponent(orderId)}`;
}

export function orderHistoryMerchantPath(): string {
  return '/order-history-merchant';
}

export function cancelOrderMerchantPath(orderId: string): string {
  return `/cancel-order-merchant?orderId=${encodeURIComponent(orderId)}`;
}

export function scheduledOrdersPath(): string {
  return '/scheduled-orders';
}

export function courierStatusPath(orderId: string): string {
  return `/courier-status?orderId=${encodeURIComponent(orderId)}`;
}

export function printTicketPath(orderId: string): string {
  return `/print-ticket?orderId=${encodeURIComponent(orderId)}`;
}

export function returnedOrderPath(orderId: string): string {
  return `/returned-order?orderId=${encodeURIComponent(orderId)}`;
}
